import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { institutionalColors } from '~/design'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import { FormattedInsight } from './insightList.screen'

const ListHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  height: 46px;
  align-items: center;
  border-bottom: 1px solid #e6e6e6;
  padding: 0 24px;

  @media (max-width: 900px) {
    padding: 0;
  }
`

const ListHeaderColumn = styled.button<{ $disabled?: boolean }>`
  display: block;
  color: ${({ $disabled }) =>
    $disabled ? institutionalColors.neutral75 : institutionalColors.neutral60};
  font-size: 0.89rem;
  font-weight: 700;
  text-transform: capitalize;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-right: 8px;
  background: transparent;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  text-align: left;
  padding-left: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:hover {
    ${({ $disabled }) => !$disabled && `color: ${institutionalColors.neutral30};`}
  }
`

const ListRow = styled.div<{ $isActive?: boolean }>`
  display: flex;
  gap: 10px;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid #e6e6e6;
  border-left: 4px solid ${({ $isActive }) => ($isActive ? '#0f438a' : 'transparent')};
  padding: 8px 24px;
  /* cursor: pointer; */
  transition: all 0.2s ease;
  background-color: ${({ $isActive }) => ($isActive ? '#f0f7ff' : 'transparent')};

  &:hover {
    background-color: ${({ $isActive }) => ($isActive ? '#e6f2ff' : '#f8f9fa')};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 900px) {
    padding: 5px 0;
    border-left: none;
    gap: 8px;
  }
`

const ListData = styled.div`
  display: block;
  color: ${institutionalColors.neutral30};
  font-size: 0.89655rem;
  font-weight: 400;

  overflow: visible;
  word-wrap: break-word;
  line-height: 1.4;
`

const ListNoDataContainer = styled.div`
  display: flex;
  min-height: 100px;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  span {
    color: #8b99a7;
    font-size: 16px;
    font-weight: 400;
  }
`

const SkeletonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid #e6e6e6;
  padding: 12px 24px;

  &:last-child {
    border-bottom: none;
  }
`

const SkeletonCell = styled.div`
  padding-right: 8px;
`

const SkeletonLine = styled.div<{ $width?: string; $height?: string; $marginBottom?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
  margin-bottom: ${({ $marginBottom }) => $marginBottom || '0'};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const CustomCellStyle = styled.div<{ $padding?: string }>`
  display: block;
  padding: ${({ $padding }) => $padding ?? '14px 6px'};
  min-width: 0;

  @media (max-width: 900px) {
    padding: 0px;
  }
`

interface CellStyle {
  width?: number
  flex?: number
}

interface CustomCellProps {
  wrapperWidth: CellStyle
  item: FormattedInsight
  label: string
  customRender?: (item: FormattedInsight) => React.ReactNode
  customPadding?: string
}

const CustomCell: React.FC<CustomCellProps> = ({
  wrapperWidth,
  item,
  label,
  customRender,
  customPadding,
}) => {
  if (customRender) {
    const renderedItems = customRender(item)
    return (
      <CustomCellStyle $padding={customPadding} style={wrapperWidth}>
        {Array.isArray(renderedItems) ? (
          renderedItems.map((content, index) =>
            React.isValidElement(content) ? (
              React.cloneElement(content as React.ReactElement, { key: index })
            ) : (
              <p key={index}>{content as any}</p>
            ),
          )
        ) : (
          <div>{renderedItems}</div>
        )}
      </CustomCellStyle>
    )
  }

  const values = (item as any)[label]

  if (Array.isArray(values)) {
    return values.map((value: string, index: number) => <ListData key={index}>{value}</ListData>)
  }

  return <ListData>{values}</ListData>
}

interface InsightListProps {
  isLoading: boolean
  items: FormattedInsight[]
  labels: Record<string, string>
  fractions?: Record<string, number>
  customCells?: Record<string, (item: FormattedInsight) => React.ReactNode>
  customCellPadding?: string
  onItemClick?: (item: FormattedInsight) => void
  expandedId?: string
  expandedLoading?: boolean
  expandedContent?: React.ReactNode
  onSortChange?: (key: string) => void
  currentSort?: { key: string; order: 'asc' | 'desc' }
  hiddenFields?: string[]
}

export default function InsightList({
  isLoading,
  items,
  labels,
  fractions,
  customCells,
  customCellPadding,
  onItemClick,
  expandedId,
  expandedLoading,
  expandedContent,
  onSortChange,
  currentSort,
  hiddenFields = [],
}: InsightListProps) {
  const refWrapper = useRef<HTMLDivElement>(null)
  const [widthState, setWidthState] = useState(0)

  useLayoutEffect(() => {
    const element = refWrapper.current
    if (!element) return

    const update = () => setWidthState(element.clientWidth)

    update()

    let resizeObserver: ResizeObserver | null = null

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(entries => {
        if (!entries || entries.length === 0) return
        const width = entries[0].contentRect.width
        setWidthState(width)
      })
      resizeObserver.observe(element)
    } else {
      window.addEventListener('resize', update)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      } else {
        window.removeEventListener('resize', update)
      }
    }
  }, [refWrapper])

  const renderList = (item: FormattedInsight, wrapperWidth: number) => {
    return Object.keys(labels).map(key => {
      if (customCells && customCells[key]) {
        return (
          <CustomCell
            key={key}
            wrapperWidth={
              fractions && fractions[key] && wrapperWidth > 0
                ? { width: wrapperWidth * fractions[key] }
                : { flex: 1 }
            }
            item={item}
            label={key}
            customRender={customCells[key]}
            customPadding={customCellPadding}
          />
        )
      }

      return (
        <ListData
          key={key}
          style={
            fractions && fractions[key] && wrapperWidth > 0
              ? { width: wrapperWidth * fractions[key] }
              : { flex: 1 }
          }
        >
          {(item as any)[key]}
        </ListData>
      )
    })
  }

  return (
    <div ref={refWrapper}>
      <ListHeaderRow>
        {Object.keys(labels).map((item: string, index: number) => (
          <ListHeaderColumn
            key={index}
            onClick={() => !isLoading && onSortChange && onSortChange(item)}
            disabled={isLoading}
            $disabled={isLoading}
            style={
              fractions && fractions[item] && widthState > 0
                ? { width: widthState * fractions[item] }
                : { flex: 1 }
            }
            aria-label={`Ordenar por ${labels[item]}`}
            title={isLoading ? 'Carregando...' : `Ordenar por ${labels[item]}`}
          >
            {labels[item]}
            {currentSort?.key === item ? (currentSort.order === 'asc' ? ' ▲' : ' ▼') : ''}
          </ListHeaderColumn>
        ))}
      </ListHeaderRow>

      {/* LOADING */}
      {isLoading ? (
        <>
          {Array.from({ length: 10 }).map((_, idx) => (
            <SkeletonRow key={idx}>
              {Object.keys(labels).map(key => {
                const style =
                  fractions && fractions[key] && widthState > 0
                    ? { width: widthState * fractions[key] }
                    : { flex: 1 }

                if (key === 'titleAndDescription') {
                  const isStateHidden = hiddenFields?.includes('state')
                  const isAlertTypeHidden = hiddenFields?.includes('alertType')

                  return (
                    <SkeletonCell key={key} style={style}>
                      <div style={{ padding: '14px 6px' }}>
                        <SkeletonLine $width='80%' $height='18px' $marginBottom='6px' />
                        {isStateHidden && (
                          <SkeletonLine $width='35%' $height='12px' $marginBottom='4px' />
                        )}
                        {isAlertTypeHidden && (
                          <SkeletonLine $width='55%' $height='12px' $marginBottom='4px' />
                        )}
                        <SkeletonLine $width='92%' $height='14px' />
                      </div>
                    </SkeletonCell>
                  )
                }

                if (key === 'alertBadge') {
                  return (
                    <SkeletonCell key={key} style={style}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <SkeletonLine $width='60%' $height='18px' />
                      </div>
                    </SkeletonCell>
                  )
                }

                if (key === 'description' || key === 'context' || key === 'variable') {
                  return (
                    <SkeletonCell key={key} style={style}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <SkeletonLine $width='85%' $height='16px' />
                        <SkeletonLine $width='70%' $height='16px' />
                      </div>
                    </SkeletonCell>
                  )
                }

                return (
                  <SkeletonCell key={key} style={style}>
                    <SkeletonLine $width='70%' $height='16px' />
                  </SkeletonCell>
                )
              })}
            </SkeletonRow>
          ))}
        </>
      ) : items && items.length > 0 ? (
        <>
          {items.map((item, index) => (
            <React.Fragment key={item.id || index}>
              <ListRow
                $isActive={item.id === expandedId}
                onClick={() => onItemClick && onItemClick(item)}
              >
                {renderList(item, widthState)}
              </ListRow>
              {item.id === expandedId && (
                <div
                  style={{
                    padding: '16px 24px',
                    borderTop: '2px solid #0f438a',
                    borderBottom: '1px solid #e6e6e6',
                    backgroundColor: expandedLoading ? '' : '#e6f2ff',
                  }}
                >
                  {expandedLoading ? (
                    <div style={{ padding: '32px 0', textAlign: 'center', color: '#6b7280' }}>
                      Carregando insight...
                      <PulseLoaderComponent />
                    </div>
                  ) : expandedContent ? (
                    expandedContent
                  ) : (
                    <div
                      style={{
                        padding: '32px 0',
                        textAlign: 'center',
                        color: '#6b7280',
                      }}
                    >
                      Insight não encontrado.
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </>
      ) : (
        <ListNoDataContainer>
          <span>Sem dados até o momento</span>
        </ListNoDataContainer>
      )}
    </div>
  )
}
